import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Container, Card, Form, Button, Alert, Spinner, Row, Col, Badge, ProgressBar, Nav, Tab
} from 'react-bootstrap';
import API from '../services/api';
import { useNavigate } from 'react-router-dom';
import { updateStoredUser } from "../utils/sessionUser";
import "./VendorProfileForm.css";

/* ========= helpers ========= */
const MAX_INLINE_MB = 5; // base64 cap per file for this form
const bytesOk = (dataUrl) => {
  const b64 = (String(dataUrl).split(',')[1] || '').trim();
  const bytes = Math.floor((b64.length * 3) / 4);
  return bytes <= MAX_INLINE_MB * 1024 * 1024;
};
const toDataURL = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onloadend = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

const isValidGST = (v = '') =>
  /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i.test(v.trim());
const isValidIFSC = (v = '') => /^[A-Z]{4}0[A-Z0-9]{6}$/i.test(v.trim());
const isValidMobile = (v = '') => /^[0-9]{10,15}$/.test(String(v || '').replace(/\D/g, '').trim());

/* ========= component ========= */
const VendorProfileForm = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [profileLocked, setProfileLocked] = useState(false);
  const [assignedManager, setAssignedManager] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [gstLookupLoading, setGstLookupLoading] = useState(false);
  const lastGstLookupRef = useRef('');

  // Base structure with all fields (Product Interest removed)
  const [form, setForm] = useState({
    /* Basic */
    companyName: '',
    gstNumber: '',
    address: '',
    turnover: '',
    logo: '',                          // small base64 or URL
    geoLocation: { lat: '', lng: '', address: '' },

    /* Contact */
    contactName: '',
    contactDesignation: '',
    contactEmail: '',
    contactMobile: '',
    whatsappNumber: '',
    website: '',
    linkedin: '',

    /* Business & Ops */
    yearEstablished: '',
    employeeCount: '',
    productionCapacity: '',           // e.g., "100 MT/month"
    factoryAddress: '',
    paymentTerms: '',                 // e.g., "Advance", "30 Days Credit"
    deliveryTerms: '',                // e.g., "FOB, CIF, Ex-Works"
    pastClients: '',                  // comma/list

    /* Legal & Compliance */
    panNumber: '',
    cinNumber: '',
    msmeNumber: '',
    fssaiNumber: '',
    iecNumber: '',

    /* Bank */
    bank: {
      accountName: '',
      accountNumber: '',
      ifsc: '',
      bankName: '',
      branch: '',
    },

    /* Documents (base64 ≤ 5MB each) */
    documents: {
      gstCertificate: '',
      panCard: '',
      msmeCertificate: '',
      cancelledCheque: '',
      itrLast3Years: '',
      bankStatementLast3Months: '',
      recentElectricityBill: '',
    }
  });

  const [logoPreview, setLogoPreview] = useState(null);

  const fileRefs = {
    logo: useRef(null),
    gstCertificate: useRef(null),
    panCard: useRef(null),
    msmeCertificate: useRef(null),
    cancelledCheque: useRef(null),
    itrLast3Years: useRef(null),
    bankStatementLast3Months: useRef(null),
    recentElectricityBill: useRef(null),
  };

  const navigate = useNavigate();

  const hasValue = (v) => String(v || '').trim().length > 0;

  const completion = useMemo(() => {
    const lat = Number(form.geoLocation?.lat);
    const lng = Number(form.geoLocation?.lng);
    const required = [
      hasValue(form.companyName),
      hasValue(form.gstNumber) && isValidGST(form.gstNumber),
      hasValue(form.address),
      hasValue(form.turnover),
      Number.isFinite(lat),
      Number.isFinite(lng),
    ];
    const requiredDone = required.filter(Boolean).length;
    const requiredTotal = required.length;
    const percent = Math.round((requiredDone / requiredTotal) * 100);

    const contactDone = [
      hasValue(form.contactName),
      hasValue(form.contactMobile),
      hasValue(form.contactEmail),
    ].filter(Boolean).length;

    const docsDone = [
      hasValue(form.documents?.gstCertificate),
      hasValue(form.documents?.panCard),
      hasValue(form.documents?.msmeCertificate),
      hasValue(form.documents?.cancelledCheque),
      hasValue(form.documents?.itrLast3Years),
      hasValue(form.documents?.bankStatementLast3Months),
      hasValue(form.documents?.recentElectricityBill),
    ].filter(Boolean).length;

    const sections = {
      basic: requiredDone === requiredTotal,
      contact: contactDone >= 1,
      business: [
        hasValue(form.yearEstablished),
        hasValue(form.employeeCount),
        hasValue(form.productionCapacity),
        hasValue(form.factoryAddress),
        hasValue(form.paymentTerms),
        hasValue(form.deliveryTerms),
        hasValue(form.pastClients),
      ].filter(Boolean).length >= 2,
      legal: [
        hasValue(form.panNumber),
        hasValue(form.cinNumber),
        hasValue(form.msmeNumber),
        hasValue(form.fssaiNumber),
        hasValue(form.iecNumber),
      ].filter(Boolean).length >= 1,
      bank: [
        hasValue(form.bank?.accountName),
        hasValue(form.bank?.accountNumber),
        hasValue(form.bank?.ifsc),
        hasValue(form.bank?.bankName),
      ].filter(Boolean).length >= 2,
      documents: docsDone >= 1,
    };

    return { percent, sections };
  }, [form]);

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await API.get('/vendor/profile', { headers });
      const v = res.data || {};

      setProfileLocked(!!v.profileLocked);
      setAssignedManager(v.assignedManager || null);

      setForm((p) => ({
        ...p,
        companyName: v.companyName || '',
        gstNumber: v.gstNumber || '',
        address: v.address || '',
        turnover: v.turnover || '',
        logo: v.logo || '',
        geoLocation: v.geoLocation || { lat: '', lng: '', address: '' },

        contactName: v.contactName || '',
        contactDesignation: v.contactDesignation || '',
        contactEmail: v.contactEmail || '',
        contactMobile: v.contactMobile || '',
        whatsappNumber: v.whatsappNumber || '',
        website: v.website || '',
        linkedin: v.linkedin || '',

        yearEstablished: v.yearEstablished || '',
        employeeCount: v.employeeCount || '',
        productionCapacity: v.productionCapacity || '',
        factoryAddress: v.factoryAddress || '',
        paymentTerms: v.paymentTerms || '',
        deliveryTerms: v.deliveryTerms || '',
        pastClients: v.pastClients || '',

        panNumber: v.panNumber || '',
        cinNumber: v.cinNumber || '',
        msmeNumber: v.msmeNumber || '',
        fssaiNumber: v.fssaiNumber || '',
        iecNumber: v.iecNumber || '',

        bank: {
          accountName: v.bank?.accountName || '',
          accountNumber: v.bank?.accountNumber || '',
          ifsc: v.bank?.ifsc || '',
          bankName: v.bank?.bankName || '',
          branch: v.bank?.branch || '',
        },

        documents: {
          gstCertificate: v.documents?.gstCertificate || '',
          panCard: v.documents?.panCard || '',
          msmeCertificate: v.documents?.msmeCertificate || '',
          cancelledCheque: v.documents?.cancelledCheque || '',
          itrLast3Years: v.documents?.itrLast3Years || v.documents?.brochure || '',
          bankStatementLast3Months:
            v.documents?.bankStatementLast3Months ||
            (Array.isArray(v.documents?.complianceCertificates) ? v.documents.complianceCertificates[0] || '' : ''),
          recentElectricityBill: v.documents?.recentElectricityBill || '',
        }
      }));

      setLogoPreview(v.logo || null);
      updateStoredUser({
        fullName: v.companyName || v.contactName || "Vendor",
        photo: v.logo || "",
        photoUpdatedAt: Date.now(),
      });
      setIsUpdate(true);
    } catch {
      setProfileLocked(false);
      setAssignedManager(null);
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          setForm((prev) => ({
            ...prev,
            geoLocation: { lat: pos.coords.latitude, lng: pos.coords.longitude, address: '' }
          })),
        () => {}
      );
    } finally {
      setLoading(false);
    }
  };

  /* ------- fetch existing (if any) ------- */
  useEffect(() => {
    (async () => {
      try {
        await loadProfile();
      } catch {
        setLoading(false);
      }
    })();
  }, []);

  /* ------- simple handlers ------- */
  const setField = (name, value) => {
    setMessage(''); setError('');
    setForm((p) => ({ ...p, [name]: value }));
  };
  const setBankField = (name, value) => {
    setMessage(''); setError('');
    setForm((p) => ({ ...p, bank: { ...p.bank, [name]: value } }));
  };
  const setDocField = (name, value) => {
    setMessage(''); setError('');
    setForm((p) => ({ ...p, documents: { ...p.documents, [name]: value } }));
  };

  const applyGstProfileDetails = (data = {}) => {
    const companyName = data.companyName || data.tradeName || data.legalName;
    const address = [data.address, data.city, data.state, data.pincode].filter(Boolean).join(', ');
    const turnover = data.gstTurnoverSlab || (data.gstTurnover ? `₹${Number(data.gstTurnover).toLocaleString('en-IN')}` : '');
    setForm((prev) => ({
      ...prev,
      companyName: companyName || prev.companyName,
      address: address || prev.address,
      turnover: turnover || prev.turnover,
      contactName: data.contactName || prev.contactName,
      contactEmail: data.email || prev.contactEmail,
      contactMobile: data.mobile || prev.contactMobile,
      panNumber: data.panNumber || prev.panNumber,
      factoryAddress: address || prev.factoryAddress,
      geoLocation: {
        ...(prev.geoLocation || {}),
        address: address || prev.geoLocation?.address || '',
      },
    }));
  };

  const runGstLookup = async (gstNumber) => {
    const gst = String(gstNumber || '').trim().toUpperCase();
    if (!isValidGST(gst) || gst === lastGstLookupRef.current || profileLocked) return;
    try {
      lastGstLookupRef.current = gst;
      setGstLookupLoading(true);
      setError('');
      const res = await API.get('/vendor/gst-lookup', { params: { gstNumber: gst } });
      applyGstProfileDetails(res.data || {});
      setMessage('GST details auto-filled.');
    } catch (err) {
      lastGstLookupRef.current = '';
      setError(err?.response?.data?.message || 'GST lookup failed.');
    } finally {
      setGstLookupLoading(false);
    }
  };

  const handleGstChange = (value) => {
    const gst = String(value || '').toUpperCase();
    setField('gstNumber', gst);
    if (isValidGST(gst)) runGstLookup(gst);
  };

  /* ------- file handlers (base64) ------- */
  const handleLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (profileLocked) {
      setError('Profile is locked. Contact your assigned manager for edits.');
      e.target.value = '';
      return;
    }
    const dataUrl = await toDataURL(file);
    if (!bytesOk(dataUrl)) {
      setError(`Logo too large. Please upload ≤ ${MAX_INLINE_MB}MB here (use Dashboard → Change Logo for bigger).`);
      e.target.value = '';
      return;
    }
    setField('logo', dataUrl);
    setLogoPreview(dataUrl);
  };

  const handleSingleDoc = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await toDataURL(file);
    if (!bytesOk(dataUrl)) {
      setError(`File too large. Please upload ≤ ${MAX_INLINE_MB}MB.`);
      e.target.value = '';
      return;
    }
    setDocField(key, dataUrl);
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation not supported.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setField('geoLocation', {
          ...(form.geoLocation || {}),
          lat: pos.coords.latitude,
          lng: pos.coords.longitude
        });
        setMessage('Location captured.');
      },
      () => setError('Please allow location access.')
    );
  };

  /* ------- validation ------- */
  const validate = () => {
    const errs = [];
    if (!String(form.companyName).trim()) errs.push('Company Name is required.');
    if (!String(form.gstNumber).trim()) errs.push('GST Number is required.');
    if (!isValidGST(form.gstNumber)) errs.push('Enter a valid GST Number (e.g., 27ABCDE1234F1Z5).');
    if (!String(form.address).trim()) errs.push('Address is required.');
    if (!String(form.turnover).trim()) errs.push('Turnover is required.');
    if (!String(form.contactMobile).trim()) errs.push('Contact mobile is required.');
    if (String(form.contactMobile).trim() && !isValidMobile(form.contactMobile)) {
      errs.push('Contact mobile must be 10-15 digits.');
    }
    const lat = Number(form.geoLocation?.lat);
    const lng = Number(form.geoLocation?.lng);
    if (!(Number.isFinite(lat) && Number.isFinite(lng))) errs.push('Valid GPS location is required.');
    if (form.bank.ifsc && !isValidIFSC(form.bank.ifsc)) errs.push('Enter a valid IFSC (e.g., HDFC0001234).');
    return errs;
  };

  /* ------- submit ------- */
  const onSubmit = async (e) => {
    e.preventDefault();
    setMessage(''); setError('');
    const errs = validate();
    if (errs.length) { setError(errs.join(' ')); return; }
    if (profileLocked) {
      setError('Profile is locked. Contact your assigned manager for edits.');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const payload = { ...form };
      // normalize numbers
      payload.geoLocation = {
        lat: Number(form.geoLocation.lat),
        lng: Number(form.geoLocation.lng),
        address: String(form.geoLocation.address || '')
      };

      // Product Interest removed → kuch bhejne ki zarurat nahi

      if (isUpdate) {
        const res = await API.put('/vendor/profile', payload, { headers });
        setMessage(res.data?.message || 'Profile updated successfully.');
        updateStoredUser({
          fullName: payload.companyName || payload.contactName || "Vendor",
          photo: payload.logo || "",
          photoUpdatedAt: Date.now(),
        });
      } else {
        const res = await API.post('/vendor/complete-profile', payload, { headers });
        setMessage(res.data?.message || 'Profile created successfully.');
        updateStoredUser({
          fullName: payload.companyName || payload.contactName || "Vendor",
          photo: payload.logo || "",
          photoUpdatedAt: Date.now(),
        });
        setIsUpdate(true);
      }
      await loadProfile();
    } catch (err) {
      if (err?.response?.data?.profileLocked) {
        setProfileLocked(true);
        setAssignedManager(err?.response?.data?.assignedManager || null);
      }
      setError(err?.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant="success" />
        <p>Loading profile…</p>
      </div>
    );
  }

  return (
    <Container fluid className="mt-4 vendor-profile">
      <Card className="p-3 shadow-sm">
        <Tab.Container defaultActiveKey="basic">
          <Row>
            {/* LEFT: Sidebar (Logo + status + Nav) */}
            <Col md={3} className="bg-light p-3" style={{ minHeight: '100vh', position: 'sticky', top: 0 }}>
              <div className="text-center mb-3">
                <div
                  style={{
                    width: 140, height: 140, borderRadius: '50%', overflow: 'hidden',
                    border: '1px solid #e6e6e6', background: '#fff', margin: '0 auto 10px'
                  }}
                >
                  <img
                    src={logoPreview || '/assets/logo.png'}
                    alt="Logo"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => { e.currentTarget.src = '/assets/logo.png'; }}
                  />
                </div>
                <input
                  ref={fileRefs.logo}
                  type="file"
                  accept="image/*"
                  className="d-none"
                  onChange={handleLogoPick}
                />
                <Button size="sm" variant="outline-secondary" onClick={() => fileRefs.logo.current?.click()} disabled={profileLocked}>
                  Change Logo
                </Button>
                <div className="mt-2">
                  <Badge bg={isUpdate ? 'success' : 'secondary'}>
                    {isUpdate ? 'Existing Profile' : 'New Profile'}
                  </Badge>
                </div>
                <div className="mt-3">
                  <div className="small text-muted mb-1">Profile completeness</div>
                  <ProgressBar now={completion.percent} label={`${completion.percent}%`} />
                </div>
              </div>

              <Nav variant="pills" className="flex-column gap-2">
                <Nav.Link eventKey="basic">
                  📌 Basic Information{' '}
                  <Badge bg={completion.sections.basic ? 'success' : 'secondary'} className="ms-2">
                    {completion.sections.basic ? 'Done' : 'Req'}
                  </Badge>
                </Nav.Link>
                <Nav.Link eventKey="contact">
                  👤 Contact{' '}
                  <Badge bg={completion.sections.contact ? 'success' : 'secondary'} className="ms-2">
                    {completion.sections.contact ? 'Added' : 'Optional'}
                  </Badge>
                </Nav.Link>
                <Nav.Link eventKey="business">
                  🏭 Business & Ops{' '}
                  <Badge bg={completion.sections.business ? 'success' : 'secondary'} className="ms-2">
                    {completion.sections.business ? 'Added' : 'Optional'}
                  </Badge>
                </Nav.Link>
                <Nav.Link eventKey="legal">
                  📑 Legal & Compliance{' '}
                  <Badge bg={completion.sections.legal ? 'success' : 'secondary'} className="ms-2">
                    {completion.sections.legal ? 'Added' : 'Optional'}
                  </Badge>
                </Nav.Link>
                <Nav.Link eventKey="bank">
                  🏦 Bank Details{' '}
                  <Badge bg={completion.sections.bank ? 'success' : 'secondary'} className="ms-2">
                    {completion.sections.bank ? 'Added' : 'Optional'}
                  </Badge>
                </Nav.Link>
                <Nav.Link eventKey="documents">
                  📂 Documents{' '}
                  <Badge bg={completion.sections.documents ? 'success' : 'secondary'} className="ms-2">
                    {completion.sections.documents ? 'Added' : 'Optional'}
                  </Badge>
                </Nav.Link>
              </Nav>
            </Col>

            {/* RIGHT: Tab Content */}
            <Col md={9} className="p-3">
              <h4 className="mb-2">{isUpdate ? 'Update Vendor Profile' : 'Complete Vendor Profile'}</h4>
              <div className="text-muted small mb-3">
                Required fields: Company Name, GST, Address, Turnover, and GPS Location.
              </div>
              {profileLocked && (
                <Alert variant="warning">
                  Profile locked. Contact your assigned manager for edits.
                  {assignedManager?.fullName ? ` ${assignedManager.fullName}` : ''}
                  {assignedManager?.mobile ? ` | ${assignedManager.mobile}` : ''}
                  {assignedManager?.email ? ` | ${assignedManager.email}` : ''}
                </Alert>
              )}
              {message && <Alert variant="success">{message}</Alert>}
              {error && <Alert variant="danger">{error}</Alert>}

              <Form onSubmit={onSubmit}>
                <fieldset disabled={profileLocked || saving}>
                <Tab.Content>
                  {/* ===== BASIC ===== */}
                  <Tab.Pane eventKey="basic">
                    <h5 className="mb-3">Basic Information</h5>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>
                            Company Name <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            value={form.companyName}
                            onChange={(e) => setField('companyName', e.target.value)}
                            required
                            placeholder="e.g. Lotus Rice Mill Pvt. Ltd."
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>
                            GST Number <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            value={form.gstNumber}
                            onChange={(e) => handleGstChange(e.target.value)}
                            isInvalid={!!form.gstNumber && !isValidGST(form.gstNumber)}
                            required
                            placeholder="27ABCDE1234F1Z5"
                          />
                          {gstLookupLoading && <Form.Text className="text-primary">Loading GST details...</Form.Text>}
                          <Form.Control.Feedback type="invalid">
                            Enter a valid GST Number.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>
                            Business Address <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            as="textarea" rows={3}
                            value={form.address}
                            onChange={(e) => setField('address', e.target.value)}
                            required
                            placeholder="Full address incl. city/state"
                          />
                        </Form.Group>
                      </Col>

                      {/* Product Interest removed */}

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>
                            Annual Turnover <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            value={form.turnover}
                            onChange={(e) => setField('turnover', e.target.value)}
                            required placeholder="₹25 lakh"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>
                            GPS Pin A <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="number" step="any"
                            value={form.geoLocation?.lat}
                            onChange={(e) => setField('geoLocation', { ...form.geoLocation, lat: e.target.value })}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>
                            GPS Pin B <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="number" step="any"
                            value={form.geoLocation?.lng}
                            onChange={(e) => setField('geoLocation', { ...form.geoLocation, lng: e.target.value })}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4} className="d-flex align-items-end">
                        <Button variant="outline-secondary" onClick={useCurrentLocation}>
                          Use Current Location
                        </Button>
                      </Col>
                      <Col md={12}>
                        <Form.Text className="text-muted">
                          Tip: Use current location to auto-fill GPS coordinates for accurate on-site verification.
                        </Form.Text>
                      </Col>
                    </Row>
                  </Tab.Pane>

                  {/* ===== CONTACT ===== */}
                  <Tab.Pane eventKey="contact">
                    <h5 className="mb-3">Primary Contact</h5>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Name</Form.Label>
                          <Form.Control
                            value={form.contactName}
                            onChange={(e) => setField('contactName', e.target.value)}
                            placeholder="e.g. Ankush"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Designation</Form.Label>
                          <Form.Control
                            value={form.contactDesignation}
                            onChange={(e) => setField('contactDesignation', e.target.value)}
                            placeholder="Owner / Manager"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            type="email"
                            value={form.contactEmail}
                            onChange={(e) => setField('contactEmail', e.target.value)}
                            placeholder="name@company.com"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Mobile</Form.Label>
                          <Form.Control
                            value={form.contactMobile}
                            onChange={(e) => setField('contactMobile', e.target.value.replace(/\D/g, '').slice(0, 15))}
                            placeholder="+91-XXXXXXXXXX"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>WhatsApp</Form.Label>
                          <Form.Control
                            value={form.whatsappNumber}
                            onChange={(e) => setField('whatsappNumber', e.target.value)}
                            placeholder="+91-XXXXXXXXXX"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Website</Form.Label>
                          <Form.Control
                            value={form.website}
                            onChange={(e) => setField('website', e.target.value)}
                            placeholder="https://example.com"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>LinkedIn</Form.Label>
                          <Form.Control
                            value={form.linkedin}
                            onChange={(e) => setField('linkedin', e.target.value)}
                            placeholder="Company or owner LinkedIn"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Tab.Pane>

                  {/* ===== BUSINESS ===== */}
                  <Tab.Pane eventKey="business">
                    <h5 className="mb-3">Business & Operations</h5>
                    <Row className="g-3">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Year of Establishment</Form.Label>
                          <Form.Control
                            type="number"
                            value={form.yearEstablished}
                            onChange={(e) => setField('yearEstablished', e.target.value)}
                            placeholder="2012"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>No. of Employees</Form.Label>
                          <Form.Control
                            type="number"
                            value={form.employeeCount}
                            onChange={(e) => setField('employeeCount', e.target.value)}
                            placeholder="25"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Production Capacity</Form.Label>
                          <Form.Control
                            value={form.productionCapacity}
                            onChange={(e) => setField('productionCapacity', e.target.value)}
                            placeholder="100 MT/month"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>Factory / Warehouse Address</Form.Label>
                          <Form.Control
                            value={form.factoryAddress}
                            onChange={(e) => setField('factoryAddress', e.target.value)}
                            placeholder="If different from business address"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Preferred Payment Terms</Form.Label>
                          <Form.Control
                            value={form.paymentTerms}
                            onChange={(e) => setField('paymentTerms', e.target.value)}
                            placeholder="Advance / 30 Days Credit"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Delivery Terms</Form.Label>
                          <Form.Control
                            value={form.deliveryTerms}
                            onChange={(e) => setField('deliveryTerms', e.target.value)}
                            placeholder="FOB / CIF / Ex-Works"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>Past Clients / References</Form.Label>
                          <Form.Control
                            as="textarea" rows={2}
                            value={form.pastClients}
                            onChange={(e) => setField('pastClients', e.target.value)}
                            placeholder="Separate multiple by commas"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Tab.Pane>

                  {/* ===== LEGAL ===== */}
                  <Tab.Pane eventKey="legal">
                    <h5 className="mb-3">Legal & Compliance</h5>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>PAN Number</Form.Label>
                          <Form.Control
                            value={form.panNumber}
                            onChange={(e) => setField('panNumber', e.target.value.toUpperCase())}
                            placeholder="ABCDE1234F"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>CIN Number</Form.Label>
                          <Form.Control
                            value={form.cinNumber}
                            onChange={(e) => setField('cinNumber', e.target.value.toUpperCase())}
                            placeholder="Company Identification Number"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>MSME / Udyam No.</Form.Label>
                          <Form.Control
                            value={form.msmeNumber}
                            onChange={(e) => setField('msmeNumber', e.target.value.toUpperCase())}
                            placeholder="e.g., UDYAM-XX-00-0000000"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>FSSAI License No.</Form.Label>
                          <Form.Control
                            value={form.fssaiNumber}
                            onChange={(e) => setField('fssaiNumber', e.target.value.toUpperCase())}
                            placeholder="Food license (if applicable)"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>IEC (Import Export Code)</Form.Label>
                          <Form.Control
                            value={form.iecNumber}
                            onChange={(e) => setField('iecNumber', e.target.value.toUpperCase())}
                            placeholder="IEC number (if applicable)"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Tab.Pane>

                  {/* ===== BANK ===== */}
                  <Tab.Pane eventKey="bank">
                    <h5 className="mb-3">Bank Details</h5>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Account Holder Name</Form.Label>
                          <Form.Control
                            value={form.bank.accountName}
                            onChange={(e) => setBankField('accountName', e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Account Number</Form.Label>
                          <Form.Control
                            value={form.bank.accountNumber}
                            onChange={(e) => setBankField('accountNumber', e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>IFSC</Form.Label>
                          <Form.Control
                            value={form.bank.ifsc}
                            onChange={(e) => setBankField('ifsc', e.target.value.toUpperCase())}
                            isInvalid={!!form.bank.ifsc && !isValidIFSC(form.bank.ifsc)}
                          />
                          <Form.Control.Feedback type="invalid">Invalid IFSC.</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Bank Name</Form.Label>
                          <Form.Control
                            value={form.bank.bankName}
                            onChange={(e) => setBankField('bankName', e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Branch</Form.Label>
                          <Form.Control
                            value={form.bank.branch}
                            onChange={(e) => setBankField('branch', e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Tab.Pane>

                  {/* ===== DOCUMENTS ===== */}
                  <Tab.Pane eventKey="documents">
                    <h5 className="mb-3">Document Uploads (≤ {MAX_INLINE_MB}MB each)</h5>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>GST Certificate</Form.Label>
                          <Form.Control
                            type="file" accept="image/*,application/pdf"
                            ref={fileRefs.gstCertificate}
                            onChange={(e) => handleSingleDoc(e, 'gstCertificate')}
                          />
                          {form.documents.gstCertificate && <small className="text-success">Attached</small>}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>PAN Card</Form.Label>
                          <Form.Control
                            type="file" accept="image/*,application/pdf"
                            ref={fileRefs.panCard}
                            onChange={(e) => handleSingleDoc(e, 'panCard')}
                          />
                          {form.documents.panCard && <small className="text-success">Attached</small>}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>MSME Certificate</Form.Label>
                          <Form.Control
                            type="file" accept="image/*,application/pdf"
                            ref={fileRefs.msmeCertificate}
                            onChange={(e) => handleSingleDoc(e, 'msmeCertificate')}
                          />
                          {form.documents.msmeCertificate && <small className="text-success">Attached</small>}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Cancelled Cheque / Passbook</Form.Label>
                          <Form.Control
                            type="file" accept="image/*,application/pdf"
                            ref={fileRefs.cancelledCheque}
                            onChange={(e) => handleSingleDoc(e, 'cancelledCheque')}
                          />
                          {form.documents.cancelledCheque && <small className="text-success">Attached</small>}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Company / Firm ITR (Last 3 Years)</Form.Label>
                          <Form.Control
                            type="file" accept="image/*,application/pdf"
                            ref={fileRefs.itrLast3Years}
                            onChange={(e) => handleSingleDoc(e, 'itrLast3Years')}
                          />
                          {form.documents.itrLast3Years && <small className="text-success">Attached</small>}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Bank Statement (Last 3 Months)</Form.Label>
                          <Form.Control
                            type="file" accept="image/*,application/pdf"
                            ref={fileRefs.bankStatementLast3Months}
                            onChange={(e) => handleSingleDoc(e, 'bankStatementLast3Months')}
                          />
                          {form.documents.bankStatementLast3Months && <small className="text-success">Attached</small>}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Recent Electricity Bill</Form.Label>
                          <Form.Control
                            type="file" accept="image/*,application/pdf"
                            ref={fileRefs.recentElectricityBill}
                            onChange={(e) => handleSingleDoc(e, 'recentElectricityBill')}
                          />
                          {form.documents.recentElectricityBill && <small className="text-success">Attached</small>}
                        </Form.Group>
                      </Col>
                    </Row>
                  </Tab.Pane>
                </Tab.Content>
                </fieldset>

                <div className="d-flex gap-2 mt-4">
                  <Button variant="success" type="submit" disabled={saving || profileLocked}>
                    {saving ? <Spinner animation="border" size="sm" /> : (isUpdate ? 'Update Profile' : 'Submit Profile')}
                  </Button>
                  <Button variant="outline-primary" type="button" onClick={() => navigate('/vendor/overview')}>
                    Go to Dashboard
                  </Button>
                </div>
              </Form>
            </Col>
          </Row>
        </Tab.Container>
      </Card>
    </Container>
  );
};

export default VendorProfileForm;
